import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const useConnectionStore = create(
  persist(
    (set, get) => ({
      candidateStates: {
        favorite: [],
        requestSent: [],
        connected: []
      },

      // Connection profiles with full data
      connectionProfiles: {
        favorite: [],
        requestSent: [],
        connected: []
      },

      isLoadingProfiles: false,

      // Fetch candidate states from API
      fetchCandidateStates: async (token) => {
        if (!token) return;

        try {
          const response = await fetch(`${API_URL}/connections/states`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          // Handle 401 Unauthorized - token expired or invalid
          if (response.status === 401) {
            const { default: useAuthStore } = await import('./authStore');
            // Clear token silently without redirecting
            const state = useAuthStore.getState();
            state.token = null;
            state.user = null;
            return;
          }

          if (response.ok) {
            const data = await response.json();
            set({
              candidateStates: {
                favorite: data.favorite || [],
                requestSent: data.requestSent || [],
                connected: data.connected || []
              }
            });
          }
        } catch (error) {
          console.error('Failed to fetch candidate states:', error);
        }
      },

      // Fetch full connection profiles
      fetchConnectionProfiles: async (token, silent = false) => {
        if (!token) return;

        if (!silent) {
          set({ isLoadingProfiles: true });
        }

        try {
          const response = await fetch(`${API_URL}/connections/profiles`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          // Handle 401 Unauthorized - token expired or invalid
          if (response.status === 401) {
            const { default: useAuthStore } = await import('./authStore');
            // Clear token silently without redirecting
            const state = useAuthStore.getState();
            state.token = null;
            state.user = null;
            set({ isLoadingProfiles: false });
            return;
          }

          const data = await response.json();
          console.log('Connection profiles response:', data);

          if (response.ok) {
            set({
              connectionProfiles: {
                favorite: data.favorite || [],
                requestSent: data.requestSent || [],
                connected: data.connected || []
              },
              isLoadingProfiles: false
            });
          } else {
            console.error('Failed to fetch connection profiles:', data);
            set({ isLoadingProfiles: false });
          }
        } catch (error) {
          console.error('Failed to fetch connection profiles:', error);
          set({ isLoadingProfiles: false });
        }
      },

      // Update candidate state locally
      updateCandidateState: (slug, state) => {
        const current = get().candidateStates;
        const currentProfiles = get().connectionProfiles;
        const timestamp = new Date().toISOString();

        // Remove from all arrays in candidateStates
        const favorite = current.favorite.filter(item => item.slug !== slug);
        const requestSent = current.requestSent.filter(item => item.slug !== slug);
        const connected = current.connected.filter(item => item.slug !== slug);

        // Remove from all arrays in connectionProfiles and find the profile
        const favoriteProfiles = currentProfiles.favorite.filter(item => item.slug !== slug);
        const requestSentProfiles = currentProfiles.requestSent.filter(item => item.slug !== slug);
        const connectedProfiles = currentProfiles.connected.filter(item => item.slug !== slug);
        
        // Find the profile from any of the arrays
        const profile = [...currentProfiles.favorite, ...currentProfiles.requestSent, ...currentProfiles.connected]
          .find(item => item.slug === slug);

        // Add to appropriate arrays if state is not null
        if (state === 'favorite') {
          favorite.push({ slug, timestamp });
          if (profile) {
            favoriteProfiles.push({ ...profile, connectionTimestamp: timestamp });
          }
        } else if (state === 'request_sent') {
          requestSent.push({ slug, timestamp });
          if (profile) {
            requestSentProfiles.push({ ...profile, connectionTimestamp: timestamp });
          }
        } else if (state === 'connected') {
          connected.push({ slug, timestamp });
          if (profile) {
            connectedProfiles.push({ ...profile, connectionTimestamp: timestamp });
          }
        }

        set({
          candidateStates: { favorite, requestSent, connected },
          connectionProfiles: {
            favorite: favoriteProfiles,
            requestSent: requestSentProfiles,
            connected: connectedProfiles
          }
        });
      },

      // Get state for a specific candidate
      getCandidateState: (slug) => {
        const states = get().candidateStates;
        if (states.connected.find(item => item.slug === slug)) return 'connected';
        if (states.requestSent.find(item => item.slug === slug)) return 'request_sent';
        if (states.favorite.find(item => item.slug === slug)) return 'favorite';
        return null;
      },

      // Get all candidates by connection type
      getCandidatesByType: (type) => {
        const states = get().candidateStates;
        return states[type] || [];
      },

      // Get connection statistics
      getStats: () => {
        const states = get().candidateStates;
        return {
          favoriteCount: states.favorite.length,
          requestSentCount: states.requestSent.length,
          connectedCount: states.connected.length,
          totalCount: states.favorite.length + states.requestSent.length + states.connected.length
        };
      },

      // Clear all connection states (call on sign out)
      clearStates: () => {
        set({
          candidateStates: {
            favorite: [],
            requestSent: [],
            connected: []
          },
          connectionProfiles: {
            favorite: [],
            requestSent: [],
            connected: []
          },
          isLoadingProfiles: false
        });
      }
    }),
    {
      name: 'connection-storage',
      partialize: (state) => ({
        candidateStates: state.candidateStates
      })
    }
  )
);

export default useConnectionStore;
